import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { SCROLL_OFFSET, SCROLL_DURATION } from 'site-modules/shared/constants/sub-navigation';

export function TcoInfoSection({ hasCollapse = true }) {
  return (
    <section className="tco-info-section text-gray-darker">
      <h2 className="heading-3">
        True Cost To Own<sup>&reg;</sup>
      </h2>

      <p className="size-16">
        Edmunds.com&apos;s True Cost to Own<sup>&reg;</sup> (TCO<sup>&reg;</sup>) is proprietary data that helps you
        estimate the total five-year cost of buying and owning a vehicle &mdash; including some items you may not have
        taken into consideration. A benefit of using our TCO<sup>&reg;</sup> tool is that you can easily compare the
        five-year totals for different vehicles and make a more informed choice.
      </p>

      <ContentCollapse
        textClosed={
          <span>
            More about TCO<sup>&reg;</sup>
          </span>
        }
        textOpen="See less"
        justify="justify-content-start"
        btnContainerClasses={classnames({ 'd-none': !hasCollapse })}
        btnClasses="px-0_5 py-0_5 size-16 text-primary-darker more-info-btn"
        btnArrowClasses="legal text-primary-darker"
        btnTextClasses="fw-normal"
        showBtnBelow
        isOpenOnLoad={!hasCollapse}
        btnTrackingId="click_more_info"
        isEclipseFade={false}
        resetParams={{
          offset: SCROLL_OFFSET,
          duration: SCROLL_DURATION,
          to: 'scrollTop-more-about',
        }}
      >
        <p className="size-16">
          The components of TCO<sup>&reg;</sup> are depreciation, interest on financing, taxes and fees, insurance
          premiums, fuel, maintenance, repairs and any federal tax credit that may be available. In order to estimate
          certain mileage-dependent costs, we assume that vehicles will be driven 15,000 miles per year. For a used
          vehicle, we calculate the years the vehicle has been driven using the nominal difference between the current
          calendar year and the vehicle&apos;s model year, and assume that it was driven 15,000 miles during each of
          those years.
        </p>

        <p className="size-16">
          Note that TCO<sup>&reg;</sup> is a comparative tool, not a predictive tool &mdash; your actual five-year cost
          of owning a particular vehicle will vary depending on your personal circumstances, such as your driving
          history and the number of miles you drive.
        </p>

        <h3 className="heading-4">
          How We Calculate True Cost to Own<sup>&reg;</sup>
        </h3>

        <p className="size-16">
          The True Cost to Own<sup>&reg;</sup> calculations use the following set of assumptions:
        </p>
        <ul>
          <li>Ownership expenses are estimated for a five-year period</li>
          <li>You will drive 15,000 miles per year</li>
          <li>You are financing the vehicle using traditional financing, not lease financing</li>
          <li>You have an above-average credit rating for the purpose of determining your finance rate</li>
          <li>You are making a 10% down payment on the vehicle at purchase</li>
          <li>Your loan term is 60 months</li>
        </ul>

        <p className="size-16">
          Using proprietary formulas, we calculate the five-year costs for the seven cost categories that make up the
          TCO<sup>&reg;</sup> (depreciation, insurance, financing, taxes &amp; fees, fuel, maintenance and repairs). We
          also take into account any applicable federal tax credit.
        </p>

        <h3 className="heading-4">
          Explanation of True Cost to Own<sup>&reg;</sup> Terms
        </h3>

        <h4 className="size-16 mb-0">Total Cash Price</h4>
        <p className="size-16">
          For new vehicles, the Total Cash Price displayed is the vehicle&apos;s True Market Value<sup>&reg;</sup> (TMV
          <sup>&reg;</sup>) price plus typically equipped options, destination charge, base tax and fees assessed by
          your state, and, if applicable, gas guzzler tax; less any widely available manufacturer-to-customer cash
          rebates. (However, we do not account for other types of cash rebates or incentives because of the variability
          of those offers and their eligibility requirements.) For used vehicles, the Total Cash Price shown is the sum
          of the vehicle&apos;s Private Party TMV<sup>&reg;</sup> price in &quot;clean&quot; condition plus typically
          equipped options, and base tax and fees assessed by your state.
        </p>

        <h4 className="size-16 mb-0">Depreciation</h4>
        <p className="size-16">
          This is the amount by which the value of a vehicle declines from its purchase price to its estimated resale
          value. The purchase price employed is the vehicle&apos;s Total Cash Price, minus any taxes and fees included
          in that amount. We estimate the resale value assuming the vehicle will be in &quot;clean&quot; condition, will
          be driven 15,000 miles per year, and will be sold to a private party.
        </p>

        <h4 className="size-16 mb-0">Insurance</h4>
        <p className="size-16">
          This is the estimated average annual insurance premium in your state. The premium has been determined based on
          annual premium data for defined driver profiles and coverages (liability, comprehensive and collision) from a
          major national insurer. While this information is specific to vehicle make, model, model year and body type,
          your personal information is not taken into consideration and could greatly alter the actual premium quoted by
          an insurer. Factors that will affect your rate include your age, marital status, credit history, driving
          record, and the garaging address of your vehicle.
        </p>

        <h4 className="size-16 mb-0">Financing</h4>
        <p className="size-16">
          This is the interest expense on a loan in the amount of the Total Cash Price, assuming a 10% down payment and
          a loan term of 60 months. The interest rate used is the prevailing rate that banks and other direct automotive
          lenders are currently charging consumers in your geographic region who have above average credit scores.
        </p>

        <p className="size-16">
          Note: Even if you do not finance your vehicle, the inclusion of financing cost in determining True Cost to Own
          <sup>&reg;</sup> is still appropriate because it reflects the estimated &quot;opportunity cost&quot; (i.e.,
          the amount you may earn) if you invest the Purchase Price instead of using it to purchase the vehicle.
        </p>

        <h4 className="size-16 mb-0">Taxes &amp; Fees</h4>
        <p className="size-16">
          This consists of the base sales (or use) taxes, license and registration fees in your state, and gas guzzler
          tax if applicable. These taxes and fees are often based on a percentage of the purchase price, and generally
          decrease as the vehicle ages and loses its value.
        </p>

        <p className="size-16">
          Note: the state sales/use tax rate that we use includes the average local and county taxes assessed in that
          state.
        </p>

        <h4 className="size-16 mb-0">Fuel</h4>
        <p className="size-16">
          This expense is based on the revised EPA mileage ratings, assuming consumption consists of 45% highway and 55%
          city driving and that the vehicle is equipped with the transmission that is standard equipment for that
          vehicle. Cost estimates are based on the current one-year moving average of self-service prices in your state,
          using regular unleaded gasoline for vehicles whose manufacturers require regular; premium unleaded gasoline
          for vehicles whose manufacturers recommend or require premium; or diesel fuel for diesel vehicles.
        </p>

        <h4 className="size-16 mb-0">Maintenance</h4>
        <p className="size-16">
          This is the estimated expense of the two types of maintenance: scheduled and unscheduled. Scheduled
          maintenance is the performance of factory-recommended items at periodic mileage and/or calendar intervals.
          Unscheduled maintenance includes wheel alignment and the replacement of items such as the battery, brakes,
          headlamps, hoses, exhaust system parts, taillight/turn signal bulbs, tires and wiper blades/inserts. Estimated
          tire replacement costs are supplied to Edmunds.com by The Tire Rack, Inc.
        </p>

        <h4 className="size-16 mb-0">Repairs</h4>

        <p className="size-16">
          This is the estimated expense for repairs not covered by the vehicle manufacturer&apos;s warranties over the
          five years from the date of purchase, assuming 15,000 miles are driven annually. We estimate this expense
          based on the cost of a typical &quot;zero deductible&quot; extended warranty for the vehicle, minus the
          estimated amount of that cost that consists of the warranty provider&apos;s overhead and profit.
        </p>

        <h4 className="size-16 mb-0">Federal Tax Credits</h4>

        <p className="size-16">
          This is the tax credit that is provided for under the Energy Policy Act of 2005. A tax credit is subtracted
          directly from the total amount of federal tax you owe. The tax credit is for electric fuel vehicles.
        </p>

        <p className="size-16">
          The credit is only available to the original purchaser of a new, qualifying vehicle, and is subject to certain
          &quot;phase out&quot; rules that we take into consideration when computing TCO<sup>&reg;</sup>. If a
          qualifying vehicle is leased to a consumer, the leasing company may claim the credit.
        </p>
      </ContentCollapse>
    </section>
  );
}

TcoInfoSection.propTypes = {
  hasCollapse: PropTypes.bool,
};
